import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Package, Tag, Image as ImgIcon, Percent, ShoppingBag, Headphones, Store, Palette, LogOut, ExternalLink, Menu } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import NotificationCenter from "@/components/NotificationCenter";

export default function TenantLayout() {
  const { slug } = useParams();
  const { user, tenant, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.title = tenant?.name ? `${tenant.name} – Admin` : "Tenant Admin";
  }, [tenant]);

  const base = `/tenant/${slug}/admin`;
  const links = [
    { to: base, label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: `${base}/products`, label: "Products", icon: Package },
    { to: `${base}/taxonomy`, label: "Categories & Brands", icon: Tag },
    { to: `${base}/banners`, label: "Banners", icon: ImgIcon },
    { to: `${base}/offers`, label: "Offers & Coupons", icon: Percent },
    { to: `${base}/orders`, label: "Orders", icon: ShoppingBag },
    { to: `${base}/ops`, label: "Leads & Tickets", icon: Headphones },
    { to: `${base}/sellers`, label: "Sellers & Staff", icon: Store },
    { to: `${base}/branding`, label: "Branding & Profile", icon: Palette },
  ];

  const SidebarContent = ({ onNavigate }) => (
    <div className="flex flex-col h-full">
      <Link to={base} onClick={onNavigate} className="px-6 py-5 border-b border-slate-200 flex items-center gap-3" data-testid="tenant-logo">
        {tenant?.branding?.logo_url ? (
          <img src={tenant.branding.logo_url} alt="logo" className="h-10 w-10 object-contain border border-slate-200 rounded p-1" />
        ) : (
          <div className="w-10 h-10 rounded bg-slate-900 grid place-items-center text-white font-bold">R</div>
        )}
        <div>
          <div className="font-display text-sm font-bold leading-none truncate max-w-[160px]">{tenant?.name}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Tenant Admin</div>
        </div>
      </Link>
      <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              data-testid={`tenant-nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            >
              <Icon className="h-4 w-4" /> {l.label}
            </NavLink>
          );
        })}
        <a
          href={`/t/${slug}`}
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          data-testid="tenant-view-storefront-link"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50"
        >
          <ExternalLink className="h-4 w-4" /> View Storefront
        </a>
      </nav>
      <div className="p-3 border-t border-slate-200">
        <div className="text-xs text-slate-500 mb-1">Signed in as</div>
        <div className="text-sm font-medium text-slate-900 mb-1" data-testid="tenant-user-name">{user?.name}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-3">{user?.role?.replace("_", " ")}</div>
        <Button size="sm" variant="outline" className="w-full" data-testid="tenant-logout-btn" onClick={() => { logout(); nav(`/tenant/${slug}/admin/login`); }}>
          <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-white border-r border-slate-200 flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-white" data-testid="tenant-mobile-drawer">
          <SidebarContent onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-x-hidden min-w-0">
        <div className="border-b border-slate-200 bg-white px-3 sm:px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:hidden min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(true)}
              data-testid="tenant-menu-toggle"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to={base} className="flex items-center gap-2 min-w-0" data-testid="tenant-mobile-logo">
              {tenant?.branding?.logo_url ? (
                <img src={tenant.branding.logo_url} alt="" className="h-7 w-7 object-contain rounded" />
              ) : (
                <div className="w-7 h-7 rounded bg-slate-900 grid place-items-center text-white font-bold text-xs">R</div>
              )}
              <div className="font-display text-sm font-bold leading-none truncate max-w-[160px]">{tenant?.name}</div>
            </Link>
          </div>
          <div className="ml-auto">
            <NotificationCenter accentColor={tenant?.branding?.primary_color || "#0F172A"} />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
